import languageService from "../services/LanguageService";
import { DateLocalizationHelper } from "./DateLocalizationHelper";

export class LanguageChangeObserver {
    private static instance: LanguageChangeObserver;
    private callbacks: Array<(locale: string) => void> = [];
    private currentLanguage: string;
    private observer: MutationObserver | null = null;

    private constructor() {
        this.currentLanguage = languageService.getCurrent();
        this.initializeObserver();
    }

    public static getInstance(): LanguageChangeObserver {
        if (!LanguageChangeObserver.instance) {
            LanguageChangeObserver.instance = new LanguageChangeObserver();
        }
        return LanguageChangeObserver.instance;
    }

    private initializeObserver(): void {
        // Watch for cookie changes (if language is stored in cookies)
        this.watchCookieChanges();
        
        // Watch for DOM changes that might indicate language switch
        this.watchDOMChanges();
    }

    private watchCookieChanges(): void {
        // Poll for cookie changes every 500ms
        setInterval(() => {
            const newLanguage = languageService.getCurrent();
            if (newLanguage !== this.currentLanguage) {
                this.handleLanguageChange(newLanguage);
            }
        }, 500);
    }

    private watchDOMChanges(): void {
        // Watch for changes in html lang attribute
        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'lang') {
                    const htmlElement = mutation.target as HTMLElement;
                    const newLang = htmlElement.getAttribute('lang');
                    if (newLang && newLang !== this.currentLanguage) {
                        // Update language service
                        languageService.setCurrent(newLang);
                        this.handleLanguageChange(newLang);
                    }
                }
            });
        });

        // Start observing
        this.observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['lang']
        });
    }

    private handleLanguageChange(newLanguage: string): void {
        console.log(`Language changed from ${this.currentLanguage} to ${newLanguage}`);
        this.currentLanguage = newLanguage;
        
        // Update the global date localization helper
        const dateHelper = DateLocalizationHelper.getInstance();
        dateHelper.setLocale(newLanguage);
        
        // Notify all registered callbacks
        this.callbacks.forEach(callback => {
            try {
                callback(newLanguage);
            } catch (error) {
                console.error('Error in language change callback:', error);
            }
        });
    }

    public subscribe(callback: (locale: string) => void): () => void {
        this.callbacks.push(callback);
        
        // Return unsubscribe function
        return () => {
            const index = this.callbacks.indexOf(callback);
            if (index > -1) {
                this.callbacks.splice(index, 1);
            }
        };
    }

    public getCurrentLanguage(): string {
        return this.currentLanguage;
    }

    public destroy(): void {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        this.callbacks = [];
    }
}

// Auto-start the observer when the module is loaded
const languageObserver = LanguageChangeObserver.getInstance();

// Export for manual access if needed
export default languageObserver;
